import type { CSSProperties, LinariaClassName } from '@linaria/core';
import { cx } from '@linaria/core';
import type { ComponentType, MouseEventHandler, Ref } from 'react';
import { User } from '../../../types';
import { useUserSelector } from '../Hooks/useUser';
import type { IconProps } from '../Icons';
import Club from '../Icons/Club';
import Group from '../Icons/Group';
import Lock from '../Icons/Lock';
import Newspaper from '../Icons/Newspaper';
import { palette } from '../styles/colors';
import BadgeAvatar from './BadgeAvatar';
import "./ModelStatusBadge.linaria.module.css!=!./ModelStatusBadge?./ModelStatusBadge.linaria.module.css?css=H4sIAAAAAAAAA3VSTY+bMBC991cgVSslVR1h2JBd9tLTnvsPLIMHmMQfrG0gibX/vQoEsrSqfPE8P7+ZNzM77hVTJ9YkSh2z0BqHHo3OLUjusYe3z2+7G+XVMerPA/YyCHSt5Je8knCenxtGm8wcu+s6ClxirQl6UC4vQXuw8w+uWC0vQ6XCTSfXRi+56pShaKv6eR0FiRpIA1g3Po9nsjox5/Xw8vKonRfOyM4velXKaHH5QBlQO/B5yWW56bndEGJR16Tm7Tb6ERG6nX/sj4w6V6BoQmGsAEssF9i5fB8/zZy0YhSyJKX2zsm/SA4ofLONnJEoognnPffckoKLGojRNzNLvpZl1wFe28A1Kj4NAHX9u5MOIrrLXATcATGdj1BXqPFhTh3nPrYGbw0m0IP2bu7oLwUCebRpLVRgHbEguhIEUWZME03xNoxikLTUDHfBf5Ev5U3q9xp0yig3nHZZsON0JseT1RFZnGbIaE1tmyahMN4bteJO0EL2ltH9UD4Pr8Fbrl1lrMrHm+QeNvv46We0j58WflWu1vARhmPnPFYXUhrtQfu/drG/MFqn7iOxYRzcqiaH18egIFmY90X8LzUR7Bhjf+jXUSh4eaqt6bQgpZHGrhRGZJE4SEaz9AVBh4n6/f39fXmMmThhexChMGfiGi7MkMfR7dDdvj1Hs+6cbpEdPtipOOj+Gq4EtYBzTt8+v/0BY2d4JgoEAAA="
const rootStyles = "atm_mk_h2mmj6 atm_9s_1txwivl atm_h_1h6ojuz atm_am_glywfm atm_g3_idpfg4";
const ringStyles = "atm_mk_stnw88 atm_f3_1byqil atm_5j_1ssbidh atm_3f_1e6231r atm_p_6zwe9p atm_mj_glywfm atm_e2p1ow_glywfm";
const badgeStyles = "atm_mk_stnw88 atm_n3_1aoa1u6 atm_6i_1g1rp32 atm_tr_15wc4w9 atm_9s_1txwivl atm_h_1h6ojuz atm_fc_1h6ojuz atm_vy_1g3sq2r atm_e2_1g3sq2r atm_5j_1ssbidh atm_2d_j0iv7v atm_7l_1638ien atm_70_dkip7d atm_wq_kb7nvz atm_mj_glywfm";
type BadgeGlyph = ComponentType<IconProps>;
const DEFAULT_BADGE_OFFSET = {
  right: 14,
  bottom: 14
};
export type ModelStatusKind = Extract<keyof User, 'lastNews' | 'isTruePrivate' | 'isOnlyPrivate' | 'isGroupShow' | 'isClubShow'>;
const STATUS_BADGES: Record<ModelStatusKind, {
  color: string;
  Glyph: BadgeGlyph;
}> = {
  lastNews: {
    color: palette.avatarBadgeNews,
    Glyph: Newspaper
  },
  isTruePrivate: {
    color: palette.avatarBadgePrivate,
    Glyph: Lock
  },
  isOnlyPrivate: {
    color: palette.avatarBadgePrivate,
    Glyph: Lock
  },
  isGroupShow: {
    color: palette.avatarBadgeGroup,
    Glyph: Group
  },
  isClubShow: {
    color: palette.avatarBadgeClub,
    Glyph: Club
  }
};
const isStatusKind = (key: keyof User): key is ModelStatusKind => key in STATUS_BADGES;
export type ModelStatusFlags = Pick<User, 'isBroadcasting' | 'isTruePrivate' | 'isPrivateShow' | 'isGroupShow' | 'isClubShow'>;
export const resolveModelStatusKind = ({
  isTruePrivate,
  isPrivateShow,
  isGroupShow,
  isClubShow
}: ModelStatusFlags): ModelStatusKind | undefined => {
  if (isTruePrivate) return 'isTruePrivate';
  if (isPrivateShow) return 'isOnlyPrivate';
  if (isGroupShow) return 'isGroupShow';
  if (isClubShow) return 'isClubShow';
  return undefined;
};
interface ModelStatusBadgeProps {
  addBoxShadow?: boolean;
  ageGate?: boolean;
  alt?: string;
  avatarClassName?: LinariaClassName;
  badgeOffset?: {
    right: number;
    bottom: number;
  };
  className?: LinariaClassName;
  disableDrag?: boolean;
  id: number;
  isOffline?: boolean;
  kind?: keyof User;
  live?: ModelStatusFlags;
  news?: boolean;
  onClick?: MouseEventHandler<HTMLSpanElement>;
  onMouseEnter?: MouseEventHandler<HTMLSpanElement>;
  onMouseLeave?: MouseEventHandler<HTMLSpanElement>;
  ref?: Ref<HTMLElement>;
  ring?: boolean;
  size: number;
  title?: string;
}
const ModelStatusBadge = (props: ModelStatusBadgeProps) => {
  const {
    addBoxShadow,
    ageGate,
    alt,
    avatarClassName,
    badgeOffset = DEFAULT_BADGE_OFFSET,
    className,
    disableDrag,
    id,
    isOffline,
    kind,
    live,
    news,
    onClick,
    onMouseEnter,
    onMouseLeave,
    ref,
    ring = true,
    size,
    title
  } = props;
  const subscribedBroadcasting = useUserSelector(live ? undefined : id, 'isBroadcasting');
  const isBroadcasting = live ? live.isBroadcasting : subscribedBroadcasting;
  const statusKind: ModelStatusKind | undefined = live ? resolveModelStatusKind(live) : news ? 'lastNews' : kind && isStatusKind(kind) ? kind : undefined;
  const badge = statusKind ? STATUS_BADGES[statusKind] : undefined;
  const showRing = ring && !badge && isBroadcasting;
  // 16 → 11, 18 → 13, 24 → 13, 26 → 14, 32 → 14, 40 → 18
  const badgeRatio = size < 24 ? 0.7 : size < 32 ? 0.55 : 0.45;
  const badgeSize = Math.round(size * badgeRatio);
  const glyphSize = Math.round(badgeSize * 0.75);
  const Glyph = badge?.Glyph;
  return <span className={cx(rootStyles, className)} style={{
    '--badge-size': `${badgeSize}px`,
    '--badge-color': badge?.color,
    '--badge-right': `${badgeOffset.right}%`,
    '--badge-bottom': `${badgeOffset.bottom}%`,
    '--ring-gap': size < 20 ? '3px' : '3.5px',
    '--ring-width': size < 20 ? '1.5px' : '2px'
  } as CSSProperties}>
            <BadgeAvatar id={id} alt={alt} ageGate={ageGate} ref={ref} size={size} title={title} isOffline={isOffline} isBroadcasting={isBroadcasting} disableBadge disableDrag={disableDrag} addBoxShadow={addBoxShadow} avatarClassName={avatarClassName} onClick={onClick} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
            {showRing && <span className={ringStyles} data-testid="ModelStatusBadge__Broadcasting" />}
            {badge && <span className={badgeStyles} data-testid={`ModelStatusBadge__${statusKind}`}>
                    {Glyph && <Glyph size={`${glyphSize}px`} color={palette.commonWhite} />}
                </span>}
        </span>;
};
export default ModelStatusBadge;